export type AmmoHelpFieldKey = 'brand' | 'name' | 'bulletType' | 'grainWeight' | 'caliber' | 'type'

export interface AmmoHelpFieldContent {
  title: string
  definition: string
  examples: string[]
  safetyNote: string
}

export const AMMO_HELP_FIELDS: AmmoHelpFieldKey[] = [
  'brand',
  'name',
  'bulletType',
  'grainWeight',
  'caliber',
  'type'
]

export const HELP_CONTENT: Record<AmmoHelpFieldKey, AmmoHelpFieldContent> = {
  brand: {
    title: 'Manufacturer (Brand)',
    definition:
      'Manufacturer name for the ammunition product. Use the company that markets the load (e.g. Federal, Hornady, Winchester), not a bullet-only brand unless you are documenting components separately.',
    examples: ['Federal', 'Hornady', 'Winchester'],
    safetyNote:
      'Accurate brand names help others find loads in the registry. Do not invent or abbreviate brands in ways that could be confused with another manufacturer.'
  },
  name: {
    title: 'Product Line',
    definition:
      'Marketing or catalog name for this specific load within the manufacturer’s lineup. This is not the cartridge name (e.g. ".308 Winchester") — it is the product family or SKU line such as Gold Medal or American Eagle.',
    examples: ['Gold Medal', 'American Eagle', 'Champion'],
    safetyNote:
      'Keep product line names factual and consistent with packaging. Avoid combining multiple unrelated product names in one entry.'
  },
  bulletType: {
    title: 'Bullet Type',
    definition:
      'Construction or purpose category of the projectile. Choose the closest match from the list (FMJ, HP, SP, BTHP, Match, Other).',
    examples: ['FMJ (range/training)', 'BTHP (match)', 'HP (defense-oriented factory load)'],
    safetyNote:
      'Bullet type describes factory ammunition classification only. Do not use this field to document handload projectile choices — use the reloading recipe form for that.'
  },
  grainWeight: {
    title: 'Bullet Weight (grains)',
    definition:
      'Nominal bullet weight in grains as printed on the box or manufacturer data. Enter a whole number when possible (e.g. 124, 175).',
    examples: ['124 gr (9mm)', '175 gr (.308 match)', '55 gr (5.56)'],
    safetyNote:
      'Verify weight against factory packaging or published specs. Incorrect grain weight can mislead compatibility and comparison searches.'
  },
  caliber: {
    title: 'Caliber',
    definition:
      'Metric or dimensional designation for the cartridge case (e.g. 9x19mm, 7.62x51mm). This belongs on the cartridge spec, not the product line field.',
    examples: ['9x19mm', '7.62x51mm', '.223 Rem (5.56x45mm)'],
    safetyNote:
      'Use standard naming where possible. When in doubt, match an existing cartridge spec in the catalog rather than creating a duplicate with a slightly different label.'
  },
  type: {
    title: 'Cartridge Type',
    definition:
      'High-level category for the cartridge: Pistol, Rifle, Shotgun, or Rimfire. Used to organize specs and filter the registry.',
    examples: ['Pistol — 9mm Luger', 'Rifle — .308 Winchester', 'Rimfire — .22 LR'],
    safetyNote:
      'Pick the category that matches typical firearm platforms for this cartridge. If unsure, check manufacturer or SAAMI-style references before submitting.'
  }
}

export function isAmmoHelpFieldKey (value: string): value is AmmoHelpFieldKey {
  return (AMMO_HELP_FIELDS as string[]).includes(value)
}
